import { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  title: string;
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  maxWidth?: number;
}

export function Modal({ title, open, onClose, children, footer, maxWidth = 520 }: ModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (open) {
      closeRef.current?.focus();
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (open) window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const overlayRef = useRef<HTMLDivElement>(null);
  // Só fecha com clique "verdadeiro" na overlay: mousedown E mouseup ambos
  // fora do modal. Evita fechar ao arrastar seleção de texto para fora.
  const pressStartedOnOverlay = useRef(false);
  const handleMouseDown = (e: React.MouseEvent) => {
    pressStartedOnOverlay.current = e.target === overlayRef.current;
  };
  const handleMouseUp = (e: React.MouseEvent) => {
    if (e.target !== overlayRef.current) pressStartedOnOverlay.current = false;
  };
  const handleClick = (e: React.MouseEvent) => {
    if (e.target === overlayRef.current && pressStartedOnOverlay.current) onClose();
  };

  if (!open) return null;

  return (
    <div
      ref={overlayRef}
      className="modal-overlay"
      onMouseDown={handleMouseDown}
      onMouseUp={handleMouseUp}
      onClick={handleClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className="modal"
        style={maxWidth ? { maxWidth } : undefined}
        onClick={(e) => e.stopPropagation()}
      >
        <header className="modal-header">
          <h2 id="modal-title" className="modal-title">{title}</h2>
          <button ref={closeRef} className="modal-close" onClick={onClose} aria-label="Fechar">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-footer">{footer}</footer>}
      </div>
    </div>
  );
}